import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useNamedLogger } from '@eluve/logger';

import { Citation } from './CitationText';
import { PdfContextState } from './PdfContext';
import { UploadedFile } from './types';

interface UsePdfManagerProps {
  tenantId: string;
  patientId: string;
  currentThreadId: string | null;
}

export const useChatFileManager = ({
  tenantId,
  patientId,
  currentThreadId,
}: UsePdfManagerProps): PdfContextState => {
  const logger = useNamedLogger('useChatFileManager');
  const apiClient = useApiClient();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [currentPreviewFile, setCurrentPreviewFile] =
    useState<UploadedFile | null>(null);

  const [showPreview, setShowPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [targetPage, setTargetPage] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightText, setHighlightText] = useState<string>('');

  const loadThreadFiles = useCallback(
    async (threadId: string) => {
      try {
        setLoading(true);
        const result = await apiClient.chat.getChatThreadFiles({
          params: { tenantId, threadId },
        });

        if (result.status === 200) {
          const { files } = result.body;
          if (files && files.length > 0) {
            setUploadedFiles(files);
          } else {
            setUploadedFiles([]);
          }
        }
      } catch (error) {
        logger.error('Failed to load thread files', { threadId, error });
        setUploadedFiles([]);
      } finally {
        setLoading(false);
      }
    },
    [apiClient, tenantId, patientId, logger],
  );

  // Text renderer for highlighting PDF text
  const textRenderer = useCallback(
    (textItem: { str: string; pageNumber: number }): string => {
      const text = textItem.str.trim().toLowerCase();
      const normalizedHighlightText = highlightText.trim().toLowerCase();
      const MIN_HIGHLIGHT_LENGTH = 15;

      if (
        !text ||
        text.length < MIN_HIGHLIGHT_LENGTH ||
        textItem.pageNumber !== targetPage
      ) {
        return textItem.str;
      }

      // Break down text into sentences and check each one
      const sentences = text
        .split(/[.!?]+/)
        .filter((sentence) => sentence.trim().length > 0);

      for (const sentence of sentences) {
        const trimmedSentence = sentence.trim();
        if (
          normalizedHighlightText.includes(trimmedSentence) ||
          trimmedSentence.includes(normalizedHighlightText)
        ) {
          return `<mark>${textItem.str}</mark>`;
        }
      }
      return textItem.str;
    },
    [highlightText, targetPage],
  );

  // PDF preview handler
  const handlePreview = useCallback(
    async (file: UploadedFile) => {
      setCurrentPreviewFile(file);
      setShowPreview(true);
      setCurrentPage(1);
      setLoading(false);
      setError(null);
      setHighlightText('');

      if (!file.fileUrl) {
        setError('File URL not available');
        logger.error('File URL missing for preview', { fileId: file.id });
      }
    },
    [logger],
  );

  const handleCitationClick = async (citation: Citation) => {
    if (uploadedFiles.length === 0) return;

    let targetPage = 1;
    let citationText = '';

    try {
      const parsed = JSON.parse(citation.text);
      targetPage = parsed.startPage ?? 1;
      citationText = parsed.text ?? '';
    } catch {
      citationText = citation.text;
    }

    // Remove extra whitespace and newlines for better matching
    citationText = citationText.trim().replace(/\s+/g, ' ');

    const targetFile = getTargetFileFromSource(citation);

    if (!targetFile) {
      logger.warn('No file found for citation', {
        citation: citation.source,
        uploadedFiles,
      });
      return;
    }

    setCurrentPreviewFile(targetFile);
    setShowPreview(true);
    setCurrentPage(targetPage);
    setTargetPage(targetPage);
    setLoading(false);
    setError(null);
    setHighlightText(citationText);

    logger.info('Opening PDF from citation', {
      fileName: targetFile.fileName,
      targetPage,
      sourceDocId: citation.source.sourceDocId,
      fileUrl: targetFile.fileUrl,
      citationText,
      highlightText: citationText,
    });

    // Validate that we have a signed URL
    if (!targetFile.fileUrl) {
      setError('File URL not available');
      logger.error('File URL missing for citation', {
        fileId: targetFile.id,
      });
    }
  };

  // PDF navigation handlers
  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const getTargetFileFromSource = (citation: Citation) => {
    if (!citation.source.sourceDocId) return undefined;

    const sourceDocId = citation.source.sourceDocId;
    const match = /([^:]+):([^_]+)(?:_|$)/.exec(sourceDocId);
    const fileId = match ? match[2] : undefined;

    return fileId ? uploadedFiles.find((f) => f.id === fileId) : undefined;
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    logger.error('Error loading PDF:', { error: error.message });
    setError('Failed to load PDF document');
    setLoading(false);
  };

  const handleRemove = useCallback(
    async (fileId: string) => {
      if (!currentThreadId) return;

      try {
        const currentFiles = uploadedFiles.map((f) => ({
          fileId: f.id,
          hasPassword: f.hasPassword,
        }));

        await apiClient.chat.updateChatThreadMetadata({
          params: { tenantId, threadId: currentThreadId },
          body: {
            userFiles: currentFiles.filter((file) => file.fileId !== fileId),
          },
        });

        setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));

        logger.info('File removed successfully', { fileId });
      } catch (error) {
        logger.error('Failed to remove file', { fileId, error });
      }
    },
    [uploadedFiles, currentThreadId, apiClient, tenantId, logger],
  );

  // Load chat thread files
  useEffect(() => {
    if (currentThreadId) {
      loadThreadFiles(currentThreadId);
    }
  }, [currentThreadId, loadThreadFiles]);

  return {
    uploadedFiles,
    setUploadedFiles,
    loadThreadFiles,
    currentPreviewFile,
    showPreview,
    setShowPreview,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    currentPage,
    totalPages,
    loading,
    error,
    textRenderer,
    handlePreview,
    handleCitationClick,
    handlePrevPage,
    handleNextPage,
    handleRemove,
  };
};
